// ───────────────────── spreadsheet import view ─────────────────────
//
// Full-screen import flow (ImportMdView's pattern): pick a theme,
// tweak column / row / cell / header-row / page-header-footer styling,
// watch the real preview — every page shown is a render of the ACTUAL
// generated PDF, so confirming locks in exactly what's on screen.
// Rebuilds are debounced engine round-trips (parsing happened once, in
// the route's file picker); the worker keeps the generated document as
// scratch state until commit/cancel.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CustomFont } from "../lib/fonts.ts";
import type { MdFace, PageInfo, RenderResult } from "../lib/protocol.ts";
import type { SheetData, SheetLayoutMeta } from "../lib/sheet-layout.ts";
import {
  SHEET_THEMES,
  cloneSheetTheme,
  sheetThemeFamilies,
  type SheetThemeConfig,
  type SheetThemePreset,
} from "../lib/sheet-theme.ts";
import { SHEET_MAX_COLS, SHEET_MAX_ROWS } from "../lib/sheet-data.ts";
import { MM_TO_PT, PAGE_PRESETS } from "../lib/ui-constants.ts";
import { ColorPicker } from "./ColorPicker.tsx";
import {
  AlignSelect,
  FontSelect,
  Num,
  PreviewPage,
  Row,
  Section,
  StyleToggles,
  familyCss,
  fieldCls,
  resolveFaces,
} from "./import-shared.tsx";

const THEME_STORE_KEY = "pdfEditorSheetImportTheme";

// ───────────────────────── stored theme ─────────────────────────

interface ThemeChoice {
  presetId: string;
  config: SheetThemeConfig;
  dirty: boolean;
}

function defaultChoice(): ThemeChoice {
  const preset = SHEET_THEMES[0]!;
  return { presetId: preset.id, config: cloneSheetTheme(preset.config), dirty: false };
}

function loadStoredChoice(): ThemeChoice {
  try {
    const raw = localStorage.getItem(THEME_STORE_KEY);
    if (!raw) return defaultChoice();
    const parsed = JSON.parse(raw) as Partial<ThemeChoice>;
    const c = parsed.config;
    if (
      !c ||
      typeof c !== "object" ||
      !c.page ||
      !c.title ||
      !c.columns ||
      !c.rows ||
      !c.cells ||
      !c.grid ||
      !c.header ||
      !c.pageHeader ||
      !c.pageFooter
    ) {
      return defaultChoice();
    }
    return {
      presetId: typeof parsed.presetId === "string" ? parsed.presetId : "clean",
      config: c as SheetThemeConfig,
      dirty: parsed.dirty === true,
    };
  } catch {
    return defaultChoice();
  }
}

// ───────────────────────── theme card ─────────────────────────

/** Theme gallery card — a CSS caricature of the preset's table. */
function ThemeCard({
  preset,
  active,
  onPick,
}: {
  preset: SheetThemePreset;
  active: boolean;
  onPick: () => void;
}) {
  const c = preset.config;
  const gridColor = c.grid.mode === "none" ? "transparent" : c.grid.color;
  const vBorder = c.grid.mode === "all" ? `1px solid ${gridColor}` : "none";
  const hBorder =
    c.grid.mode === "all" || c.grid.mode === "horizontal"
      ? `1px solid ${gridColor}`
      : "none";
  return (
    <button
      type="button"
      onClick={onPick}
      title={preset.blurb}
      className={`flex flex-col gap-1 rounded-md border p-1.5 text-left ${
        active
          ? "border-[color:var(--color-accent)] ring-1 ring-[color:var(--color-accent)]"
          : "border-[color:var(--color-border)] hover:border-[color:var(--color-border-strong)]"
      }`}
    >
      <div
        className="flex h-20 w-full flex-col gap-1 overflow-hidden rounded-sm border border-[color:var(--color-border)] p-1.5"
        style={{ background: c.page.background }}
      >
        {c.title.show && (
          <span
            className="truncate leading-none"
            style={{
              color: c.title.color,
              fontFamily: familyCss(c.title.family),
              fontWeight: c.title.bold ? 700 : 400,
              fontStyle: c.title.italic ? "italic" : "normal",
              fontSize: 10,
            }}
          >
            Sheet 1
          </span>
        )}
        <div
          className="w-full overflow-hidden rounded-[1px]"
          style={{ outline: c.grid.mode === "outline" ? `1px solid ${gridColor}` : "none" }}
        >
          <div className="flex" style={{ background: c.header.background, borderBottom: hBorder }}>
            {[0, 1, 2].map((i) => (
              <span key={i} className="flex-1 px-1 py-[3px]" style={{ borderRight: i < 2 ? vBorder : "none" }}>
                <span
                  className="block h-[3px] w-4/5 rounded-xs"
                  style={{ background: c.header.color, opacity: 0.9 }}
                />
              </span>
            ))}
          </div>
          {[0, 1, 2].map((r) => (
            <div
              key={r}
              className="flex"
              style={{
                background: r % 2 === 1 && c.rows.stripeBackground ? c.rows.stripeBackground : "transparent",
                borderBottom: r < 2 ? hBorder : "none",
              }}
            >
              {[0, 1, 2].map((i) => (
                <span key={i} className="flex-1 px-1 py-[3px]" style={{ borderRight: i < 2 ? vBorder : "none" }}>
                  <span
                    className="block h-[3px] rounded-xs"
                    style={{
                      width: `${[85, 60, 70][((r + i) % 3)]}%`,
                      marginLeft: i > 0 ? "auto" : 0,
                      background: c.cells.color,
                      opacity: 0.45,
                    }}
                  />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
      <span className="text-[11px] font-medium leading-tight">{preset.label}</span>
    </button>
  );
}

// ───────────────────────── the view ─────────────────────────

export function ImportSheetView({
  name,
  sheets,
  truncated,
  customFonts,
  build,
  renderPage,
  commit,
  onCancel,
  onCommit,
}: {
  name: string;
  sheets: SheetData[];
  truncated: boolean;
  customFonts: CustomFont[];
  build: (args: {
    sheets: SheetData[];
    theme: SheetThemeConfig;
    faces: MdFace[];
    meta: SheetLayoutMeta;
  }) => Promise<{ pages: PageInfo[] }>;
  renderPage: (pageIndex: number, scale: number) => Promise<RenderResult>;
  commit: () => Promise<ArrayBuffer>;
  onCancel: () => void;
  onCommit: (bytes: ArrayBuffer, pageCount: number) => void;
}) {
  // ── theme state (restored from the last import) ──
  const [choice, setChoice] = useState<ThemeChoice>(loadStoredChoice);
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORE_KEY, JSON.stringify(choice));
    } catch {
      /* storage full/blocked — theme just won't persist */
    }
  }, [choice]);

  const config = choice.config;
  const update = useCallback((fn: (c: SheetThemeConfig) => void) => {
    setChoice((prev) => {
      const next = cloneSheetTheme(prev.config);
      fn(next);
      return { ...prev, config: next, dirty: true };
    });
  }, []);
  const pickPreset = useCallback((id: string) => {
    const preset = SHEET_THEMES.find((p) => p.id === id);
    if (!preset) return;
    setChoice({ presetId: id, config: cloneSheetTheme(preset.config), dirty: false });
  }, []);

  // ── sheet selection (multi-sheet workbooks) ──
  const [picked, setPicked] = useState<boolean[]>(() => sheets.map(() => true));
  const selectedSheets = useMemo(
    () => sheets.filter((_, i) => picked[i]),
    [sheets, picked]
  );

  const meta = useMemo<SheetLayoutMeta>(
    () => ({
      file: name,
      date: new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
    }),
    [name]
  );

  // ── build loop (debounced; generation-guarded) ──
  const [pages, setPages] = useState<PageInfo[] | null>(null);
  const [renderGen, setRenderGen] = useState(0);
  const [busy, setBusy] = useState(true);
  const [buildError, setBuildError] = useState<string | null>(null);
  const genRef = useRef(0);
  const hasBuiltRef = useRef(false);

  useEffect(() => {
    const gen = (genRef.current += 1);
    if (selectedSheets.length === 0) {
      setPages(null);
      setBusy(false);
      return;
    }
    setBusy(true);
    const timer = setTimeout(
      () => {
        void (async () => {
          try {
            const faces = await resolveFaces(sheetThemeFamilies(config), customFonts);
            if (genRef.current !== gen) return;
            const res = await build({ sheets: selectedSheets, theme: config, faces, meta });
            if (genRef.current !== gen) return;
            hasBuiltRef.current = true;
            setPages(res.pages);
            setRenderGen(gen);
            setBuildError(null);
          } catch (err) {
            if (genRef.current === gen) {
              setBuildError(err instanceof Error ? err.message : String(err));
            }
          } finally {
            if (genRef.current === gen) setBusy(false);
          }
        })();
      },
      hasBuiltRef.current ? 250 : 0
    );
    return () => clearTimeout(timer);
  }, [selectedSheets, config, customFonts, build, meta]);

  // ── preview zoom ──
  const previewRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<"fit" | number>("fit");
  const [previewW, setPreviewW] = useState(800);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setPreviewW(el.clientWidth));
    ro.observe(el);
    setPreviewW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const pageW = pages?.[0]?.width ?? PAGE_PRESETS.A4![0];
  const scale =
    zoom === "fit" ? Math.max(0.2, Math.min(1.6, (previewW - 64) / pageW)) : zoom;

  // ── commit ──
  const [committing, setCommitting] = useState(false);
  const doImport = useCallback(() => {
    if (busy || committing || !pages || pages.length === 0) return;
    setCommitting(true);
    void (async () => {
      try {
        const bytes = await commit();
        onCommit(bytes, pages.length);
      } catch (err) {
        setCommitting(false);
        setBuildError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [busy, committing, pages, commit, onCommit]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  // ── customisation sections ──
  const [openSection, setOpenSection] = useState<string | null>(null);
  const toggle = (id: string) => setOpenSection((s) => (s === id ? null : id));

  const mm = (pt: number) => Math.round(pt / MM_TO_PT);
  const totalRows = useMemo(
    () => selectedSheets.reduce((n, s) => n + s.rows.length, 0),
    [selectedSheets]
  );

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-[color:var(--color-bg)]">
      {/* header */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[color:var(--color-border)] px-3 py-2">
        <i className="ri-table-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Import Spreadsheet</h2>
        <span className="min-w-0 truncate text-xs text-[color:var(--color-fg-muted)]">{name}</span>
        <span className="text-xs text-[color:var(--color-fg-muted)]">
          {selectedSheets.length === 0
            ? "no sheets selected"
            : pages === null
              ? "building…"
              : `${selectedSheets.length} sheet${selectedSheets.length === 1 ? "" : "s"} · ${totalRows} row${totalRows === 1 ? "" : "s"} · ${pages.length} page${pages.length === 1 ? "" : "s"}`}
          {busy && pages !== null && " · updating…"}
        </span>
        {truncated && (
          <span
            className="text-xs text-amber-600"
            title={`Only the first ${SHEET_MAX_ROWS.toLocaleString()} rows × ${SHEET_MAX_COLS} columns per sheet are imported.`}
          >
            <i className="ri-scissors-cut-line" aria-hidden /> large workbook — trimmed
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={typeof zoom === "number" ? String(zoom) : "fit"}
            onChange={(e) =>
              setZoom(e.target.value === "fit" ? "fit" : Number(e.target.value))
            }
            className="h-7 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-xs"
            title="Preview zoom"
          >
            <option value="fit">Fit width</option>
            <option value="0.75">75%</option>
            <option value="1">100%</option>
            <option value="1.25">125%</option>
            <option value="1.5">150%</option>
          </select>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || committing || !pages || pages.length === 0}
            onClick={doImport}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-40"
          >
            <i className="ri-file-transfer-line" aria-hidden />
            {committing ? "Importing…" : "Import as PDF"}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* sidebar: themes + customisation */}
        <div className="flex w-80 shrink-0 flex-col gap-3 overflow-y-auto border-r border-[color:var(--color-border)] p-3">
          <div>
            <div className="mb-1.5 flex items-center">
              <p className="text-xs font-medium">Theme</p>
              {choice.dirty && (
                <button
                  type="button"
                  onClick={() => pickPreset(choice.presetId)}
                  className="ml-auto text-[11px] text-[color:var(--color-accent)] hover:underline"
                  title="Discard customisations and restore the preset"
                >
                  Reset customisations
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SHEET_THEMES.map((preset) => (
                <ThemeCard
                  key={preset.id}
                  preset={preset}
                  active={choice.presetId === preset.id}
                  onPick={() => pickPreset(preset.id)}
                />
              ))}
            </div>
          </div>

          {sheets.length > 1 && (
            <div>
              <p className="mb-1.5 text-xs font-medium">Sheets</p>
              <div className="space-y-1 rounded-md border border-[color:var(--color-border)] p-2">
                {sheets.map((s, i) => (
                  <label key={i} className="flex cursor-pointer items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={picked[i] ?? false}
                      onChange={(e) =>
                        setPicked((prev) => prev.map((v, j) => (j === i ? e.target.checked : v)))
                      }
                    />
                    <span className="min-w-0 flex-1 truncate">{s.name}</span>
                    <span className="text-[10px] text-[color:var(--color-fg-muted)]">
                      {s.rows.length} × {s.rows.reduce((n, r) => Math.max(n, r.length), 0)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs font-medium">
            Customise
            {choice.dirty && (
              <span className="ml-1 font-normal text-[color:var(--color-fg-muted)]">(edited)</span>
            )}
          </p>

          <Section title="Page" open={openSection === "page"} onToggle={() => toggle("page")}>
            <Row label="Size">
              <select
                value={config.page.preset}
                onChange={(e) => update((c) => (c.page.preset = e.target.value as never))}
                className={`${fieldCls} flex-1`}
              >
                {Object.keys(PAGE_PRESETS).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <select
                value={config.page.landscape ? "landscape" : "portrait"}
                onChange={(e) => update((c) => (c.page.landscape = e.target.value === "landscape"))}
                className={`${fieldCls} flex-1`}
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </Row>
            <Row label="Margins mm">
              <Num
                title="Top / bottom"
                value={mm(config.page.marginTop)}
                min={5}
                max={60}
                step={1}
                onChange={(v) =>
                  update((c) => {
                    c.page.marginTop = v * MM_TO_PT;
                    c.page.marginBottom = v * MM_TO_PT;
                  })
                }
              />
              <Num
                title="Left / right"
                value={mm(config.page.marginLeft)}
                min={5}
                max={60}
                step={1}
                onChange={(v) =>
                  update((c) => {
                    c.page.marginLeft = v * MM_TO_PT;
                    c.page.marginRight = v * MM_TO_PT;
                  })
                }
              />
            </Row>
            <Row label="Background">
              <ColorPicker
                value={config.page.background}
                onChange={(v) => update((c) => (c.page.background = v))}
                title="Page background"
              />
            </Row>
          </Section>

          <Section title="Title" open={openSection === "title"} onToggle={() => toggle("title")}>
            <Row label="Show">
              <input
                type="checkbox"
                checked={config.title.show}
                onChange={(e) => update((c) => (c.title.show = e.target.checked))}
              />
              {config.title.show && (
                <select
                  value={config.title.source}
                  onChange={(e) => update((c) => (c.title.source = e.target.value as never))}
                  className={`${fieldCls} flex-1`}
                >
                  <option value="sheet">Sheet name</option>
                  <option value="file">File name</option>
                </select>
              )}
            </Row>
            {config.title.show && (
              <>
                <Row label="Font">
                  <FontSelect
                    value={config.title.family}
                    onChange={(v) => update((c) => (c.title.family = v))}
                    customFonts={customFonts}
                  />
                </Row>
                <Row label="Size / colour">
                  <Num
                    value={config.title.size}
                    min={8}
                    max={40}
                    onChange={(v) => update((c) => (c.title.size = v))}
                  />
                  <ColorPicker
                    value={config.title.color}
                    onChange={(v) => update((c) => (c.title.color = v))}
                    title="Title colour"
                  />
                  <StyleToggles
                    bold={config.title.bold}
                    italic={config.title.italic}
                    onBold={(v) => update((c) => (c.title.bold = v))}
                    onItalic={(v) => update((c) => (c.title.italic = v))}
                  />
                </Row>
                <Row label="Align">
                  <AlignSelect
                    value={config.title.align}
                    onChange={(v) => update((c) => (c.title.align = v))}
                  />
                </Row>
                <Row label="Rule below">
                  <input
                    type="checkbox"
                    checked={config.title.ruleColor !== null}
                    onChange={(e) =>
                      update((c) => (c.title.ruleColor = e.target.checked ? "#d0d7de" : null))
                    }
                  />
                  {config.title.ruleColor !== null && (
                    <ColorPicker
                      value={config.title.ruleColor}
                      onChange={(v) => update((c) => (c.title.ruleColor = v))}
                      title="Rule colour"
                    />
                  )}
                </Row>
              </>
            )}
          </Section>

          <Section title="Columns" open={openSection === "columns"} onToggle={() => toggle("columns")}>
            <Row label="Widths">
              <select
                value={config.columns.widthMode}
                onChange={(e) => update((c) => (c.columns.widthMode = e.target.value as never))}
                className={`${fieldCls} flex-1`}
                title="How the page width is divided between columns"
              >
                <option value="auto">Fit content</option>
                <option value="fill">Fill page width</option>
                <option value="equal">Equal widths</option>
              </select>
            </Row>
            <Row label="Align">
              <select
                value={config.columns.align}
                onChange={(e) => update((c) => (c.columns.align = e.target.value as never))}
                className={`${fieldCls} flex-1`}
                title="Auto: numbers & dates right, booleans centre, text left"
              >
                <option value="auto">Auto (by type)</option>
                <option value="left">Left</option>
                <option value="center">Centre</option>
                <option value="right">Right</option>
              </select>
            </Row>
          </Section>

          <Section title="Rows" open={openSection === "rows"} onToggle={() => toggle("rows")}>
            <Row label="Stripes">
              <input
                type="checkbox"
                checked={config.rows.stripeBackground !== null}
                onChange={(e) =>
                  update((c) => (c.rows.stripeBackground = e.target.checked ? "#f6f8fa" : null))
                }
              />
              {config.rows.stripeBackground !== null && (
                <ColorPicker
                  value={config.rows.stripeBackground}
                  onChange={(v) => update((c) => (c.rows.stripeBackground = v))}
                  title="Stripe background"
                />
              )}
            </Row>
            <Row label="Blank rows">
              <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                <input
                  type="checkbox"
                  checked={config.rows.skipBlank}
                  onChange={(e) => update((c) => (c.rows.skipBlank = e.target.checked))}
                />
                Skip empty rows
              </label>
            </Row>
          </Section>

          <Section title="Cells" open={openSection === "cells"} onToggle={() => toggle("cells")}>
            <Row label="Font">
              <FontSelect
                value={config.cells.family}
                onChange={(v) => update((c) => (c.cells.family = v))}
                customFonts={customFonts}
              />
            </Row>
            <Row label="Size / colour">
              <Num
                value={config.cells.size}
                min={5}
                max={16}
                onChange={(v) => update((c) => (c.cells.size = v))}
              />
              <ColorPicker
                value={config.cells.color}
                onChange={(v) => update((c) => (c.cells.color = v))}
                title="Cell text colour"
              />
            </Row>
            <Row label="Line height">
              <Num
                value={config.cells.lineHeight}
                min={1}
                max={2.5}
                step={0.05}
                onChange={(v) => update((c) => (c.cells.lineHeight = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">× size</span>
            </Row>
            <Row label="Cell pad">
              <Num
                title="Horizontal"
                value={config.cells.paddingX}
                min={1}
                max={20}
                step={1}
                onChange={(v) => update((c) => (c.cells.paddingX = v))}
              />
              <Num
                title="Vertical"
                value={config.cells.paddingY}
                min={1}
                max={20}
                step={1}
                onChange={(v) => update((c) => (c.cells.paddingY = v))}
              />
            </Row>
            <Row label="Long text">
              <select
                value={config.cells.wrap ? "wrap" : "clip"}
                onChange={(e) => update((c) => (c.cells.wrap = e.target.value === "wrap"))}
                className={`${fieldCls} flex-1`}
              >
                <option value="wrap">Wrap onto multiple lines</option>
                <option value="clip">Clip with …</option>
              </select>
            </Row>
            <Row label="Grid">
              <select
                value={config.grid.mode}
                onChange={(e) => update((c) => (c.grid.mode = e.target.value as never))}
                className={`${fieldCls} flex-1`}
              >
                <option value="all">Full grid</option>
                <option value="horizontal">Horizontal lines</option>
                <option value="outline">Outline only</option>
                <option value="none">None</option>
              </select>
            </Row>
            {config.grid.mode !== "none" && (
              <Row label="Grid style">
                <ColorPicker
                  value={config.grid.color}
                  onChange={(v) => update((c) => (c.grid.color = v))}
                  title="Grid line colour"
                />
                <Num
                  value={config.grid.width}
                  min={0.25}
                  max={3}
                  step={0.25}
                  onChange={(v) => update((c) => (c.grid.width = v))}
                  title="Grid line thickness, pt"
                />
              </Row>
            )}
          </Section>

          <Section title="Header row" open={openSection === "header"} onToggle={() => toggle("header")}>
            <Row label="First row">
              <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                <input
                  type="checkbox"
                  checked={config.header.enabled}
                  onChange={(e) => update((c) => (c.header.enabled = e.target.checked))}
                />
                Treat as header
              </label>
            </Row>
            {config.header.enabled && (
              <>
                <Row label="Repeat">
                  <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                    <input
                      type="checkbox"
                      checked={config.header.repeat}
                      onChange={(e) => update((c) => (c.header.repeat = e.target.checked))}
                    />
                    On every page
                  </label>
                </Row>
                <Row label="Background">
                  <ColorPicker
                    value={config.header.background}
                    onChange={(v) => update((c) => (c.header.background = v))}
                    title="Header background"
                  />
                  <ColorPicker
                    value={config.header.color}
                    onChange={(v) => update((c) => (c.header.color = v))}
                    title="Header text colour"
                  />
                  <StyleToggles
                    bold={config.header.bold}
                    italic={config.header.italic}
                    onBold={(v) => update((c) => (c.header.bold = v))}
                    onItalic={(v) => update((c) => (c.header.italic = v))}
                  />
                </Row>
                <Row label="Size">
                  <Num
                    value={config.header.size}
                    min={5}
                    max={18}
                    onChange={(v) => update((c) => (c.header.size = v))}
                  />
                  <span className="text-[10px] text-[color:var(--color-fg-muted)]">pt</span>
                </Row>
                <Row label="Rule below">
                  <input
                    type="checkbox"
                    checked={config.header.ruleColor !== null}
                    onChange={(e) =>
                      update((c) => (c.header.ruleColor = e.target.checked ? "#8c959f" : null))
                    }
                  />
                  {config.header.ruleColor !== null && (
                    <ColorPicker
                      value={config.header.ruleColor}
                      onChange={(v) => update((c) => (c.header.ruleColor = v))}
                      title="Rule colour"
                    />
                  )}
                </Row>
              </>
            )}
          </Section>

          <Section
            title="Page header & footer"
            open={openSection === "pageText"}
            onToggle={() => toggle("pageText")}
          >
            <Row label="Header">
              <input
                type="text"
                value={config.pageHeader.text}
                placeholder="(none)"
                onChange={(e) => update((c) => (c.pageHeader.text = e.target.value))}
                className={`${fieldCls} flex-1`}
              />
            </Row>
            {config.pageHeader.text && (
              <Row label="">
                <AlignSelect
                  value={config.pageHeader.align}
                  onChange={(v) => update((c) => (c.pageHeader.align = v))}
                />
                <Num
                  value={config.pageHeader.size}
                  min={5}
                  max={14}
                  onChange={(v) => update((c) => (c.pageHeader.size = v))}
                  title="Size, pt"
                />
                <ColorPicker
                  value={config.pageHeader.color}
                  onChange={(v) => update((c) => (c.pageHeader.color = v))}
                  title="Header text colour"
                />
              </Row>
            )}
            <Row label="Footer">
              <input
                type="text"
                value={config.pageFooter.text}
                placeholder="(none)"
                onChange={(e) => update((c) => (c.pageFooter.text = e.target.value))}
                className={`${fieldCls} flex-1`}
              />
            </Row>
            {config.pageFooter.text && (
              <Row label="">
                <AlignSelect
                  value={config.pageFooter.align}
                  onChange={(v) => update((c) => (c.pageFooter.align = v))}
                />
                <Num
                  value={config.pageFooter.size}
                  min={5}
                  max={14}
                  onChange={(v) => update((c) => (c.pageFooter.size = v))}
                  title="Size, pt"
                />
                <ColorPicker
                  value={config.pageFooter.color}
                  onChange={(v) => update((c) => (c.pageFooter.color = v))}
                  title="Footer text colour"
                />
              </Row>
            )}
            <p className="text-[10px] text-[color:var(--color-fg-muted)]">
              Placeholders: {"{page} {pages} {sheet} {file} {date}"}
            </p>
          </Section>

          <p className="text-[10px] text-[color:var(--color-fg-muted)]">
            Numbers keep the source file's formatting (dates, currencies,
            percentages). Each sheet starts a new page and becomes a PDF
            bookmark. The preview is the generated PDF itself — importing
            keeps it exactly as shown.
          </p>
        </div>

        {/* preview */}
        <div ref={previewRef} className="min-w-0 flex-1 overflow-auto bg-[color:var(--color-bg-subtle)]">
          {buildError ? (
            <div className="p-6 text-sm text-red-600">Could not build the preview: {buildError}</div>
          ) : selectedSheets.length === 0 ? (
            <div className="p-6 text-sm text-[color:var(--color-fg-muted)]">
              Select at least one sheet to import.
            </div>
          ) : pages === null ? (
            <div className="p-6 text-sm text-[color:var(--color-fg-muted)]">
              <i className="ri-loader-4-line animate-spin" aria-hidden /> Building preview…
            </div>
          ) : (
            <div className="flex flex-col items-center gap-7 p-6 pb-10">
              {pages.map((info, i) => (
                <PreviewPage
                  key={i}
                  pageIndex={i}
                  info={info}
                  scale={scale}
                  gen={renderGen}
                  renderPage={renderPage}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
